import { PagedItems } from '@texas/types';
import { httpClient } from '../httpClient/httpClient';
import { ArticleState } from './articlesApi';
import { ProductGroup } from './metadata/productGroupsApi';
import { SlimTag } from './tagsApi';

export interface RecommendedSuppliers {
  productGroup: string | null;
  state: ArticleState;
  suppliers: SupplierListItem[];
}

export interface SupplierListItem {
  id: number;
  name: string;
  archived: string | null;
  isInternal: boolean;
  isItlSupplier: boolean;
  activeArticles: number;
  alreadyAdded: boolean;
  disabled: boolean;
  articleCount: number;
  syncedArticleCount: number;
  lastArticleCreated: string | null;
  supplierNumbers: { number: string; branchIdentifier: string }[] | null;
  tags: SlimTag[];
}

export interface AddSupplierRequest {
  name: string;
  isInternal: boolean;
  isItlSupplier: boolean;
}

export interface Supplier {
  id: number;
  name: string;
  dmixIdentifier: string;
  email: string;
  organizationId: number;
  folderId: number;
  documentsFolderId: number;
  emailsFolderId: number;
  samplesFolderId: number;
  archived: boolean;
  isInternal: boolean;
  isItlSupplier: boolean;
  branchData: SupplierBranchData[];
  productGroups: ProductGroup[] | null;
}

interface SupplierBranchFields {
  supplierNumber: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  telephone: string | null;
  email: string | null;
  website: string | null;
  countryId: number | null;
  deliveryMethodId: number | null;
  deliveryTermId: number | null;
  paymentTermId: number | null;
  currencyId: number | null;
  buyingRate: number | null;
  useCustomBuyingRate: boolean;
}

export interface UpdateSupplierBranchDataRequest extends SupplierBranchFields {
  enabled: boolean;
}

export interface SupplierBranchData extends SupplierBranchFields {
  supplierId: number;
  branchId: number;
  enabledAt: string | null;
}

export interface SupplierSearchQuery {
  organizationId: number;
  searchTerm: string;
  includeArchived: boolean;
  sortDesc: boolean;
  sortBy: string;
  page: number;
  pageSize: number;
  isInternal?: boolean;
  isItlSupplier?: boolean;
  supplierIds?: number[];
  branchIds?: number[];
  complianceDocumentDefinitionIds?: number[];
  tagIds?: number[];
}

export interface SearchSuppliersResponse {
  items: PagedItems<SupplierListItem>;
  supplierIds: number[];
}

export interface ContactsSearchQuery {
  searchTerm: string;
  includeArchived: boolean;
  sortBy: string;
  sortDesc: boolean;
}

export interface SupplierContact {
  id: number;
  name: string;
  email: string;
  archived?: string | null;
}

export interface UpdateSupplierRequest {
  name: string;
  dmixIdentifier: string;
  isInternal: boolean;
  isItlSupplier: boolean;
}

export interface SupplierProductGroupDto {
  productGroupId: number;
  value: string;
  code: string;
  articleCount: number;
  sampleLeadTime: number | null;
  actualSampleLeadTime: number | null;
  bulkLeadTime: number | null;
  shipmentLeadTime: number | null;
}

export interface BulkAddProductGroupsRequest {
  productGroups: Array<{
    productGroupId: number;
    sampleLeadTime: number | null;
    bulkLeadTime: number | null;
    shipmentLeadTime: number | null;
  }>;
}

export interface UpdateProductGroupLeadTimesRequest {
  sampleLeadTime: number | null;
  bulkLeadTime: number | null;
  shipmentLeadTime: number | null;
}

export interface SupplierArticlesFilters {
  branchIds?: number[];
  productGroupIds?: number[];
  brandIds?: number[];
  isMainPurchaseInfo?: boolean;
  isMainVariant?: boolean;
  isSynced?: boolean;
  garpState?: ArticleState;
  sortBy: string;
  sortDesc: boolean;
  page: number;
  pageSize: number;
}

export interface SupplierArticleItem {
  articleId: number;
  variantId: number;
  itemCode: string;
  variantName: string;
  customerId: number;
  customerName: string;
  projectNumber: string | null;
  isMainPurchaseInfo: boolean;
  isSynced: boolean;
  created: string;
  productGroupName: string | null;
  branchId: number;
  branchIdentifier: string;
  productDeveloperName: string | null;
  garpState: ArticleState | null;
  isMainVariant: boolean;
}

export interface SupplierCurrencyDto {
  id: number;
  branchId: number;
  code: string;
  name: string;
  sellingRate: number;
  buyingRate: number;
  branchBuyingRate: number;
  isSupplierCustom: boolean;
  isDefaultBranchCurrency: boolean;
}

export const suppliersApi = {
  searchSuppliers: (params: SupplierSearchQuery) =>
    httpClient.get<SearchSuppliersResponse>('suppliers/search', {
      params,
    }),
  get: (id: number) => httpClient.get<Supplier>(`suppliers/${id}`),
  getAll: () => httpClient.get<Supplier[]>(`suppliers`),
  update: (id: number, request: UpdateSupplierRequest) =>
    httpClient.put<Supplier>(`suppliers/${id}`, { data: request }),
  updateBranchInfo: (
    request: UpdateSupplierBranchDataRequest,
    supplierId: number,
    branchId: number,
  ) =>
    httpClient.put<SupplierBranchData>(
      `suppliers/${supplierId}/branches/${branchId}`,
      { data: request },
    ),
  createSupplier: (data: AddSupplierRequest) =>
    httpClient.post<Supplier>(`suppliers`, { data }),
  getContacts: (supplierId: number, params: ContactsSearchQuery) =>
    httpClient.get<SupplierContact[]>(`${supplierId}/contacts`, { params }),
  getArticles: (supplierId: number, filters: SupplierArticlesFilters) =>
    httpClient.post<PagedItems<SupplierArticleItem>>(
      `suppliers/${supplierId}/articles`,
      {
        data: filters,
      },
    ),
  variant: {
    getRecommendedSuppliers: (variantId: number, branchId: number) =>
      httpClient.get<RecommendedSuppliers>(
        `suppliers/variant/${variantId}/recommended/${branchId}`,
      ),
    getAllBranchSuppliers: (variantId: number, branchId: number) =>
      httpClient.get<SupplierListItem[]>(
        `suppliers/variant/${variantId}/all/${branchId}`,
      ),
  },
  archive: (id: number) => httpClient.put<Supplier>(`suppliers/${id}/archive`),
  restore: (id: number) => httpClient.put<Supplier>(`suppliers/${id}/restore`),
  downloadExcel: (data: { organizationId: number; supplierIds?: number[] }) =>
    httpClient.post<any>('excel/suppliers/export', {
      data,
      responseType: 'blob',
    }),
  productGroups: {
    get: (supplierId: number, sortBy = 'productGroup', sortDesc = false) =>
      httpClient.get<SupplierProductGroupDto[]>(
        `suppliers/${supplierId}/product-groups?sortBy=${sortBy}&sortDesc=${sortDesc}`,
      ),
    bulkAdd: (supplierId: number, request: BulkAddProductGroupsRequest) =>
      httpClient.post<SupplierProductGroupDto[]>(
        `suppliers/${supplierId}/product-groups/bulk`,
        { data: request },
      ),
    update: (
      supplierId: number,
      productGroupId: number,
      request: UpdateProductGroupLeadTimesRequest,
    ) =>
      httpClient.put<SupplierProductGroupDto>(
        `suppliers/${supplierId}/product-groups/${productGroupId}`,
        { data: request },
      ),
    remove: (supplierId: number, productGroupId: number) =>
      httpClient.delete(
        `suppliers/${supplierId}/product-groups/${productGroupId}`,
      ),
  },
  getSupplierCurrenciesForBranch: (supplierId: number, branchId: number) =>
    httpClient.get<SupplierCurrencyDto[]>(
      `suppliers/${supplierId}/branches/${branchId}/currencies`,
    ),
};
